import {
  CLAIM_TYPE_CATEGORY,
  getDefaultClaimCategory,
  isSingleClaimCategoryEnv,
  shouldShowCaseCategoryPicker,
} from "../constants/claimTypeCategory";

export { shouldShowCaseCategoryPicker };

export const MOTOR_ROUTE_SUFFIX = "-Motor";

export const CASE_SECTION_MENU_KEYS = ["active_case", "my_case"];

export const isMotorCaseRoute = (pathname = "") =>
  pathname.endsWith(MOTOR_ROUTE_SUFFIX);

/** List routes end with -Motor; detail routes use /Segment-Motor/:id */
export const isMotorCaseContextPath = (pathname = "") => {
  if (isMotorCaseRoute(pathname)) return true;
  return /-Motor\//.test(pathname);
};

export const stripMotorRouteSuffix = (pathname = "") =>
  isMotorCaseRoute(pathname)
    ? pathname.slice(0, -MOTOR_ROUTE_SUFFIX.length)
    : pathname;

export const appendMotorRouteSuffix = (path = "") => {
  if (!path || path.endsWith(MOTOR_ROUTE_SUFFIX)) return path;
  return `${path}${MOTOR_ROUTE_SUFFIX}`;
};

export const applyCaseCategoryToPath = (path, category) => {
  const basePath = stripMotorRouteSuffix(path);
  return category === CLAIM_TYPE_CATEGORY.MOTOR
    ? appendMotorRouteSuffix(basePath)
    : basePath;
};

export const getDefaultCaseSectionUrl = (menuKey) => {
  if (menuKey === "my_case") return "/My-Case-Initiated";
  return "/Initiated";
};

export const isCaseSectionMenuItem = (menuItems, item) => {
  if (!item) return false;
  if (CASE_SECTION_MENU_KEYS.includes(item.key)) return true;

  let current = item;
  while (current?.parentKey) {
    const parent = findMenuItemByKey(menuItems, current.parentKey);
    if (!parent) break;
    if (CASE_SECTION_MENU_KEYS.includes(parent.key)) return true;
    current = parent;
  }
  return false;
};

const findMenuItemByKey = (menuItems, key) => {
  if (!menuItems || !key) return null;
  for (let i = 0; i < menuItems.length; i++) {
    if (menuItems[i].key === key) return menuItems[i];
    const found = findMenuItemByKey(menuItems[i].children, key);
    if (found) return found;
  }
  return null;
};

const MY_CASE_ROUTE_TO_ENDPOINT = {
  Initiated: "My-Case-Initiated",
  "On-Progress": "My-Case-On-Progress",
  "Assign-Field-Officer": "My-Case-Assign-Field-Officer",
  "Accepted-By-FO": "My-Case-Accepted-By-FO",
  "Rejected-By-FO": "My-Case-Rejected-By-FO",
  "Field-QC-Pending": "My-Case-Field-QC-Pending",
  "Waiting-for-Doctor-Allocation": "My-Case-Waiting-for-Doctor-Allocation",
  "Doctor-Report-Pending": "My-Case-Doctor-Report-Pending",
  "Manager-QC-Pending": "My-Case-Manager-QC-Pending",
  "Team-Leader-QC-Pending": "My-Case-Team-Leader-QC-Pending",
  "Waiting-For-Closer": "My-Case-Waiting-For-Closer",
  Closed: "My-Case-Closed",
};

const ACTIVE_ROUTE_TO_ENDPOINT = {
  "/Active-Case": "Initiated",
  "/Initiated": "Initiated",
  "/On-Progress": "On-Progress",
  "/Assign-Field-Officer": "Assign-Field-Officer",
  "/Accepted-By-FO": "Accepted-By-FO",
  "/Rejected-By-FO": "Rejected-By-FO",
  "/Field-QC-Completed": "Field-QC-Pending",
  "/Waiting-for-Doctor-Allocation": "Waiting-for-Doctor-Allocation",
  "/Doctor-Report-Pending": "Doctor-Report-Pending",
  "/Manager-QC-Pending": "Manager-QC-Pending",
  "/Team-Leader-QC-Pending": "Team-Leader-QC-Pending",
  "/Waiting-For-Closer": "Waiting-For-Closer",
  "/Closed": "Closed",
};

export const resolveCaseApiEndpoint = (pathname, case_url = "") => {
  const basePath = stripMotorRouteSuffix(pathname);
  const isMotor = isMotorCaseRoute(pathname);
  const isMyCaseRoute = basePath.startsWith("/My-Case");

  let endpoint;

  if (isMyCaseRoute) {
    if (basePath === "/My-Case-Initiated") {
      endpoint = "My-Case-Initiated";
    } else {
      const routePart = basePath.replace("/My-Case-", "");
      endpoint = MY_CASE_ROUTE_TO_ENDPOINT[routePart] || `My-Case-${routePart}`;
    }
  } else if (basePath === "/Active-Case") {
    endpoint = "Initiated";
  } else {
    endpoint = ACTIVE_ROUTE_TO_ENDPOINT[basePath] || case_url;
  }

  if (isMotor && endpoint) {
    return `${endpoint}${MOTOR_ROUTE_SUFFIX}`;
  }

  return endpoint;
};

export const pathsMatchCaseRoute = (currentPath, menuPath) => {
  if (currentPath == null || menuPath == null) return false;
  const current = String(currentPath);
  const menu = String(menuPath);
  if (!current || !menu) return false;
  const normalizedCurrent = stripMotorRouteSuffix(
    current.replace(process.env.PUBLIC_URL || "", "")
  );
  const normalizedMenu = menu.replace(process.env.PUBLIC_URL || "", "");
  return normalizedCurrent === normalizedMenu;
};

export const getCaseClaimCategoryFromPath = (pathname = "") =>
  isMotorCaseContextPath(pathname)
    ? CLAIM_TYPE_CATEGORY.MOTOR
    : CLAIM_TYPE_CATEGORY.HEALTH;

/** health | motor — from route, or sole env category when picker is skipped */
export const resolveCaseClaimCategory = (pathname = "") => {
  if (isSingleClaimCategoryEnv()) {
    return getDefaultClaimCategory();
  }
  return getCaseClaimCategoryFromPath(pathname);
};

export const getCaseSummaryPath = (caseId, pathname = "") => {
  const category = resolveCaseClaimCategory(pathname);
  return category === CLAIM_TYPE_CATEGORY.MOTOR
    ? `/Case-Summary-Motor/${caseId}`
    : `/Case-Summary/${caseId}`;
};

export const getDynamicReportPath = (caseId, pathname = "") => {
  const category = resolveCaseClaimCategory(pathname);
  return category === CLAIM_TYPE_CATEGORY.MOTOR
    ? `/Dynamic-Report-Motor/${caseId}`
    : `/Dynamic-Report/${caseId}`;
};

export const hasCasePermissions = (permissions) =>
  Boolean(
    permissions &&
      typeof permissions === "object" &&
      Object.values(permissions).some(Boolean)
  );

export const hasCaseMenuData = (menuData) =>
  Array.isArray(menuData) && menuData.length > 0;

/** Until motor list API returns its own permissions/menu, reuse health values. */
export const resolveActiveCasePermissions = (isMotor, motorPermissions, healthPermissions) =>
  isMotor && hasCasePermissions(motorPermissions)
    ? motorPermissions
    : healthPermissions;

export const resolveActiveCaseMenuData = (isMotor, motorMenuData, healthMenuData) =>
  isMotor && hasCaseMenuData(motorMenuData) ? motorMenuData : healthMenuData;

export const resolveActiveCaseStatusCount = (isMotor, motorCounts, healthCounts) =>
  isMotor && Array.isArray(motorCounts) && motorCounts.length > 0
    ? motorCounts
    : healthCounts;
